import { Box, Heading } from '@wix/design-system';
import { useTranslation } from '@wix/wix-i18n-config';
import classNames from 'classnames';
import { FunctionComponent, useContext } from 'react';

import { Context } from '../../../context';
import { DATA_HOOKS } from '../../../dataHooks';
import {
  ItemOnPageEventSource,
  useBiOnVisibleItem,
} from '../../../hooks/useBiOnVisibleItem';
import { useIsGenAnswer } from '../../../hooks/useIsGenAnswer';
import { ArticleSearchResult } from '../../../types';
import { isMobile } from '../../../utils/userAgent';
import { RenderHTML } from '@wix/cc-user-facing-common-components';
import css from '../index.module.scss';

type SearchResultsHeaderProps = {
  term: string;
  searchResults: ArticleSearchResult;
  displayGeneratedAnswer: boolean;
  displayGeneratedAnswerLoading: boolean;
};

export const SearchResultsHeader: FunctionComponent<SearchResultsHeaderProps> = ({
  term,
  searchResults,
  displayGeneratedAnswer,
  displayGeneratedAnswerLoading,
}) => {
  const { t } = useTranslation();
  const context  = useContext(Context);
  const isMobileView = isMobile(context);
  const { itemRef } = useBiOnVisibleItem(
    ItemOnPageEventSource.MORE_SEARCH_RESULTS
  );
  const isNextGenSearchEnabled = useIsGenAnswer();

  const { itemsCount } = searchResults;

  const resultsTitle =
    itemsCount > 1
      ? t('search-results-page.title', { count: itemsCount, term })
      : t('search-results-page.title-single', { term });

  const genAnswerResultsTitleGrey = t('search-results-page.title-answer-gen-more');
  const genAnswerResultsTitleBlack = ` ${t('search-results-page.title-answer-gen-term', { term })}`;

  return <Box
    className={classNames(css.searchResultsHeader, {
      [css.nextGenSearchDisabled]: !isNextGenSearchEnabled,
    })}
    verticalAlign="middle"
    align="space-between"
  >
    {!displayGeneratedAnswerLoading && (
      <Heading
        appearance={!displayGeneratedAnswer ? 'H4' : 'H3'}
        ellipsis
        dataHook={DATA_HOOKS.SEARCH_RESULTS_TITLE}
        className={css.searchResultsTitle}
      >
        {!displayGeneratedAnswer ? (
          <RenderHTML
          html={resultsTitle}
          className={css.searchResultsTitleHTML}
          textElement={
            isMobileView
            ? {
              ellipsis: true,
              maxLines: 3,
            }
            : undefined
          }
          />
        ) : (
          <Box
            dataHook={DATA_HOOKS.MORE_RESULTS}
            className={classNames(css.searchResultsTitleHTML, {
              [css.generatedAnswerWide]: displayGeneratedAnswer,
            })}
            ref={itemRef}
            direction="horizontal"
          >
            <RenderHTML
              className={css.searchResultsTitleGrey}
              html={genAnswerResultsTitleGrey}
              headingElement={{ size: 'large' }}
            />
            <RenderHTML
              html={genAnswerResultsTitleBlack}
              headingElement={{ ellipsis: true, size: 'large' }}
            />
          </Box>
        )}
      </Heading>
    )}
  </Box>;
  };

